import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { UploadCloud, Video, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";

import {
  AddVideoCollectionThunk,
  UpdateVideoCollectionThunk,
  GetSingleVideoCollectionThunk,
} from "../../redux/states/videoCollection/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { AllProductsThunk } from "../../redux/states/products/thunk";

const CreateUpdateVideoCollection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const { products } = useSelector((state) => state.products);
  const { singlevideocollection } = useSelector(
    (state) => state.videocollection
  );

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [videoFiles, setVideoFiles] = useState([]);

  const [formData, setFormData] = useState({
    uuid: "",
    product_id: "",
    title: "",
    link: "",
    status: "active",
  });

  const API_URL = process.env.REACT_APP_API_URL;

  /* ================= LOAD PRODUCTS ================= */
  useEffect(() => {
    dispatch(AllProductsThunk());
  }, [dispatch]);

  /* ================= LOAD SINGLE DATA (EDIT) ================= */
  useEffect(() => {
    if (uuid) {
      dispatch(GetSingleVideoCollectionThunk(uuid));
    }
  }, [dispatch, uuid]);

  /* ================= SET DATA FOR EDIT ================= */
  useEffect(() => {
    if (singlevideocollection) {
      setFormData({
        uuid: singlevideocollection.uuid,
        product_id: singlevideocollection.product_id || "",
        title: singlevideocollection.title || "",
        link: singlevideocollection.link || "",
        status: singlevideocollection.status || "active",
      });

      if (singlevideocollection.vid_path) {
        setPreview(`${API_URL}/${singlevideocollection.vid_path}`);
      }
    }
  }, [singlevideocollection, API_URL]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setVideoFiles(files);

    if (files.length > 0) {
      setPreview(URL.createObjectURL(files[0]));
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
       payload.append("uuid", formData.uuid);
      payload.append("product_id", formData.product_id);
      payload.append("title", formData.title);
      payload.append("link", formData.link);
      payload.append("status", formData.status);

      // Backend expects array
      videoFiles.forEach((file) => {
        payload.append("vid_path[]", file);
      });

      if (uuid) {
        payload.append("uuid", formData.uuid);
        await dispatch(UpdateVideoCollectionThunk(payload)).unwrap();
        toast.success("Video updated successfully 🎉");
      } else {
        await dispatch(AddVideoCollectionThunk(payload)).unwrap();
        toast.success("Video added successfully 🎉");
      }

      navigate("/video-collection");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-12xl mx-auto bg-white rounded-xl shadow-lg p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Video className="text-indigo-600" />
              {uuid ? "Update Video Collection" : "Add Video Collection"}
            </h2>

            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-indigo-600"
            >
              <ArrowLeft size={18} />
              Back
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Product */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Select Product
              </label>
              <select
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
                required
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">-- Select Product --</option>
                {products?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Link */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Link
              </label>
              <input
                type="text"
                name="link"
                value={formData.link}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Upload Video
              </label>

              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleFileChange}
                className="w-full border rounded-lg px-3 py-2"
              />

              {/* Preview */}
              {preview && (
                <div className="mt-4">
                  <video
                    src={preview}
                    controls
                    className="w-72 rounded-xl border shadow"
                  />
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
              >
                {loading ? "Saving..." : uuid ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateVideoCollection;
